import { IonIcon } from '@ionic/react';
import { pause, videocam } from 'ionicons/icons';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setActiveInstance,
  setPersistentPlayerVisible,
  setPlaying,
  setURL,
} from '../../store/slices/audio-player-state.slice';
import { showUpgradeModal } from '../../store/slices/ui.slice';
import { RootState } from '../../store/store';
import './MediaPlayer.scss';

interface MediaPlayerProps {
  url?: string;
  loadUrl?: () => Promise<string>;
  height: number;
  changeTrack?: (direction: 'next' | 'previous') => void;
  autoplay?: boolean;
  redacted?: boolean;
  isVideo?: boolean;
}

interface MediaPlayerHandle {
  play: () => Promise<void>;
  moveToStart: () => void;
  moveToEnd: () => void;
}

/**
 * Row-level control for the app-wide video player.
 *
 * The actual video element lives in StickyPlayer so a virtualized track row
 * can unmount without stopping playback.
 */
const MediaPlayer = forwardRef<MediaPlayerHandle, MediaPlayerProps>(
  ({ url: initialUrl, loadUrl, redacted = false }, ref) => {
    const dispatch = useDispatch();
    const activeInstance = useSelector(
      (state: RootState) => state.audioPlayerState.activeInstance,
    );
    const globalPlaying = useSelector(
      (state: RootState) => state.audioPlayerState.isPlaying,
    );
    const [url, setUrl] = useState(initialUrl);
    const [loading, setLoading] = useState(false);
    const [previewUnavailable, setPreviewUnavailable] = useState(false);
    const instanceId = useRef(`media-player-${crypto.randomUUID()}`).current;
    const isActive = activeInstance === instanceId;

    useEffect(() => {
      setUrl(initialUrl);
      setPreviewUnavailable(false);
    }, [initialUrl]);

    const resolveUrl = useCallback(async (): Promise<string | undefined> => {
      if (url) return url;
      if (!loadUrl || previewUnavailable) return undefined;

      setLoading(true);
      try {
        const resolvedUrl = await loadUrl();
        setUrl(resolvedUrl);
        return resolvedUrl;
      } catch (error) {
        console.error('Video preview URL could not be loaded:', error);
        setPreviewUnavailable(true);
        return undefined;
      } finally {
        setLoading(false);
      }
    }, [loadUrl, previewUnavailable, url]);

    const startPlayback = useCallback(async () => {
      if (redacted) {
        dispatch(showUpgradeModal());
        return;
      }

      const resolvedUrl = await resolveUrl();
      if (!resolvedUrl) return;

      dispatch(setURL(resolvedUrl));
      dispatch(setActiveInstance(instanceId));
      dispatch(setPersistentPlayerVisible(true));
      dispatch(setPlaying(true));
    }, [dispatch, instanceId, redacted, resolveUrl]);

    const togglePlay = () => {
      if (isActive && globalPlaying) {
        dispatch(setPlaying(false));
        dispatch(setPersistentPlayerVisible(false));
        return;
      }

      void startPlayback();
    };

    const getPersistentVideo = (): HTMLVideoElement | null =>
      document.querySelector('.persistent-player.video-player video');

    useImperativeHandle(ref, () => ({
      play: startPlayback,
      moveToStart: () => {
        const video = getPersistentVideo();
        if (video) video.currentTime = 0;
      },
      moveToEnd: () => {
        const video = getPersistentVideo();
        if (video && Number.isFinite(video.duration)) {
          video.currentTime = video.duration;
        }
      },
    }));

    return (
      <div className="media-player-button">
        <IonIcon
          icon={isActive && globalPlaying ? pause : videocam}
          onClick={togglePlay}
          className="video-play-icon"
          aria-label={
            previewUnavailable
              ? 'Video preview unavailable'
              : isActive && globalPlaying
                ? 'Pause video preview'
                : 'Play video preview'
          }
          aria-disabled={previewUnavailable}
          data-loading={loading || undefined}
        />
      </div>
    );
  },
);

MediaPlayer.displayName = 'MediaPlayer';

export default MediaPlayer;
export type { MediaPlayerHandle, MediaPlayerProps };
